import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { ShoppingBag, User, Menu, X, Plus, Minus, Trash2, Package, BarChart3, LogOut, Check, AlertCircle, Edit, ChevronLeft, Star, Mail, TrendingUp, Users, HardHat } from "lucide-react";

// Use relative URL so it works on both preview and production
const API = "/api";

// Configure axios
axios.defaults.withCredentials = true;

// Contexts
const AuthContext = createContext(null);
const CartContext = createContext(null);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Cart Provider
const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });

  // Get or create cart session from localStorage
  const getCartSession = () => {
    let session = localStorage.getItem('crude_cart_session');
    if (!session) {
      session = crypto.randomUUID();
      localStorage.setItem('crude_cart_session', session);
    }
    return session;
  };

  const cartHeaders = () => ({ 'X-Cart-Session': getCartSession() });

  const fetchCart = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, { headers: cartHeaders() });
      setCart(data);
    } catch {
      setCart({ items: [], total: 0 });
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1, variant = null) => {
    const { data } = await axios.post(
      `${API}/cart/add`,
      { product_id: productId, quantity, variant },
      { headers: cartHeaders() }
    );
    if (data.session_id) localStorage.setItem('crude_cart_session', data.session_id);
    await fetchCart();
    // Trigger abandoned cart email prompt if no email captured yet
    if (!localStorage.getItem('crude_email_captured')) {
      setTimeout(() => window.dispatchEvent(new CustomEvent('show-email-prompt')), 500);
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    await axios.put(`${API}/cart/${itemId}?quantity=${quantity}`, null, { headers: cartHeaders() });
    await fetchCart();
  };

  const removeFromCart = async (itemId) => {
    await axios.delete(`${API}/cart/${itemId}`, { headers: cartHeaders() });
    await fetchCart();
  };

  const clearCart = async () => {
    await axios.delete(`${API}/cart`, { headers: cartHeaders() });
    await fetchCart();
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart, getCartSession }}>
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => useContext(CartContext);

// Navigation Component
const Navigation = () => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = user?.role === 'admin';
  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="nav-main">
      <div className="nav-container">
        <Link to="/" className="nav-logo" data-testid="nav-logo">CRUDE DESIGNS</Link>
        
        <div className={`nav-links ${mobileOpen ? 'show' : ''}`}>
          <Link to="/shop" className={`nav-link ${location.pathname === '/shop' ? 'active' : ''}`} data-testid="nav-shop">Shop</Link>
          <Link to="/shop?category=hats" className="nav-link" data-testid="nav-hats">Hats</Link>
          <Link to="/reviews" className={`nav-link ${location.pathname === '/reviews' ? 'active' : ''}`} data-testid="nav-reviews">Reviews</Link>
          <Link to="/track-order" className={`nav-link ${location.pathname === '/track-order' ? 'active' : ''}`} data-testid="nav-track">Track Order</Link>
          {isAdmin && <Link to="/admin" className="nav-link" data-testid="nav-admin">Admin</Link>}
        </div>

        <div className="nav-icons">
          <Link to="/cart" className="nav-icon-btn" data-testid="nav-cart">
            <ShoppingBag size={20} />
            {cartCount > 0 && <span className="cart-badge" data-testid="cart-badge">{cartCount}</span>}
          </Link>
          {user ? (
            <button onClick={handleLogout} className="nav-icon-btn" data-testid="nav-logout">
              <LogOut size={20} />
            </button>
          ) : (
            <Link to="/login" className="nav-icon-btn" data-testid="nav-login">
              <User size={20} />
            </Link>
          )}
          <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
    </nav>
  );
};

// Order Tracking Page (public)
const TrackOrder = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/track-order`, { params: { order_number: orderNumber, email } });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Order not found');
    }
    setLoading(false);
  };

  return (
    <div className="fade-in" style={{ paddingTop: '100px', minHeight: '100vh' }}>
      <section className="section" style={{ maxWidth: '600px' }}>
        <div className="section-header">
          <h2 className="section-title">TRACK YOUR ORDER</h2>
          <p className="section-subtitle">Enter your order number and email to check status.</p>
        </div>

        <form onSubmit={handleSubmit} style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', padding: '2rem', marginBottom: '1.5rem' }}>
          <div className="form-group">
            <label className="form-label">Order Number</label>
            <input 
              className="form-input" 
              placeholder="CD-XXXXXXXX"
              value={orderNumber}
              onChange={e => setOrderNumber(e.target.value.toUpperCase())}
              required
              data-testid="track-order-number"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input 
              type="email"
              className="form-input" 
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              data-testid="track-order-email"
            />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading} data-testid="track-submit">
            {loading ? 'Looking up...' : 'Track Order'}
          </button>
        </form>

        {error && <div className="auth-error">{error}</div>}

        {result && (
          <div style={{ background: 'var(--crude-charcoal)', border: '2px solid var(--crude-accent)', padding: '2rem' }} data-testid="track-result">
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.75rem', letterSpacing: '0.15em', color: 'var(--crude-white-dim)' }}>ORDER</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--crude-accent)' }}>{result.order_number}</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--crude-white-dim)', marginTop: '0.25rem' }}>
                Placed {new Date(result.created_at).toLocaleDateString()}
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.75rem', letterSpacing: '0.15em', color: 'var(--crude-white-dim)', marginBottom: '0.5rem' }}>STATUS</div>
              <div style={{ padding: '1rem', background: 'var(--crude-black)', border: '1px solid var(--crude-gray)', lineHeight: 1.6 }}>
                <div style={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: '0.5rem' }}>
                  {result.status?.replace(/_/g, ' ')}
                </div>
                <div style={{ color: 'var(--crude-white-dim)', fontSize: '0.9rem' }}>
                  {result.status_message}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.75rem', letterSpacing: '0.15em', color: 'var(--crude-white-dim)', marginBottom: '0.5rem' }}>ITEMS</div>
              {result.items?.map((item, i) => (
                <div key={i} style={{ padding: '0.5rem 0', borderBottom: i < result.items.length - 1 ? '1px solid var(--crude-gray)' : 'none', fontSize: '0.9rem' }}>
                  {item.quantity}× {item.product_name}
                  {item.variant?.color && <span style={{ color: 'var(--crude-white-dim)' }}> · {item.variant.color}</span>}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '1rem', borderTop: '1px solid var(--crude-gray)', fontWeight: 700 }}>
              <span>Total</span>
              <span>${result.total?.toFixed(2)} CAD</span>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
};

// All Reviews Page
const AllReviews = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(r => setData(r.data)).catch(() => setData({ reviews: [], count: 0, average_rating: 0 }));
  }, []);

  if (!data) return <div style={{ paddingTop: '100px' }}><div className="loading"><div className="spinner" /></div></div>;

  return (
    <div className="fade-in" style={{ paddingTop: '100px', minHeight: '100vh' }}>
      <section className="section" style={{ maxWidth: '900px' }}>
        <div className="section-header">
          <h2 className="section-title">CUSTOMER REVIEWS</h2>
          {data.count > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}>
              <StarRating rating={Math.round(data.average_rating)} readonly size={28} />
              <p style={{ color: 'var(--crude-white-dim)', fontSize: '1rem' }}>
                <strong style={{ color: 'var(--crude-white)', fontSize: '1.25rem' }}>{data.average_rating}</strong> out of 5 · {data.count} review{data.count !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>

        {data.reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <p style={{ color: 'var(--crude-white-dim)', marginBottom: '1.5rem', fontSize: '1rem' }}>No reviews yet. Be the first to rate a hat!</p>
            <Link to="/shop" className="btn btn-primary">Shop Hats</Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {data.reviews.map(review => (
              <div key={review.id} style={{ 
                background: 'var(--crude-charcoal)', 
                border: '1px solid var(--crude-gray)', 
                padding: '1.5rem',
                display: 'flex',
                gap: '1.5rem',
                flexWrap: 'wrap'
              }} data-testid={`all-review-${review.id}`}>
                {review.product_image && (
                  <Link to={`/product/${review.product_id}`} style={{ flexShrink: 0 }}>
                    <div style={{ width: '80px', height: '80px', overflow: 'hidden', background: 'var(--crude-gray)' }}>
                      <img src={review.product_image} alt={review.product_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  </Link>
                )}
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <Link to={`/product/${review.product_id}`} style={{ color: 'var(--crude-accent)', textDecoration: 'none', fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                        {review.product_name}
                      </Link>
                      <p style={{ margin: '0.25rem 0', fontWeight: 600 }}>{review.name}</p>
                    </div>
                    <span style={{ color: 'var(--crude-white-dim)', fontSize: '0.8rem' }}>
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <StarRating rating={review.rating} readonly size={14} />
                  <p style={{ marginTop: '0.75rem', marginBottom: 0, color: 'var(--crude-white-dim)', lineHeight: 1.6 }}>{review.comment}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
};

// Companies Section - Who Reps CRUDE Designs
const CompaniesSection = () => {
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    axios.get(`${API}/companies`).then(r => setCompanies(r.data)).catch(() => setCompanies([]));
  }, []);

  return (
    <section className="section crew-section">
      <div className="section-header">
        <h2 className="section-title">THE CREW</h2>
        <p className="section-subtitle">Companies who rep CRUDE Designs. Alberta businesses. Blue collar built.</p>
      </div>
      {companies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 2rem', maxWidth: '600px', margin: '0 auto' }}>
          <p style={{ color: 'var(--crude-white-dim)', marginBottom: '1.5rem', fontSize: '1.1rem' }}>
            Companies repping our merch will be featured here.
          </p>
          <a href="mailto:Alberta.crude@outlook.ca?subject=Wholesale Inquiry" className="btn btn-secondary" data-testid="wholesale-inquiry-btn">
            Wholesale Inquiries
          </a>
        </div>
      ) : (
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.5rem' }}>
          {companies.map(company => (
            <div key={company.id} style={{ 
              background: 'var(--crude-charcoal)', 
              border: '1px solid var(--crude-gray)', 
              padding: '2rem', 
              textAlign: 'center',
              transition: 'all 0.3s'
            }} data-testid={`company-${company.id}`}>
              {company.logo_url ? (
                <div style={{ height: '80px', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={company.logo_url} alt={company.name} style={{ maxHeight: '100%', maxWidth: '100%', filter: 'brightness(0) invert(1)' }} />
                </div>
              ) : (
                <div style={{ height: '80px', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: 'var(--crude-accent)', fontWeight: 700 }}>
                  {company.name?.[0] || 'C'}
                </div>
              )}
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>{company.name}</h3>
              {company.industry && <p style={{ color: 'var(--crude-accent)', fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 0.5rem' }}>{company.industry}</p>}
              {company.location && <p style={{ color: 'var(--crude-white-dim)', fontSize: '0.85rem', margin: 0 }}>{company.location}</p>}
            </div>
          ))}
        </div>
      )}
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <p style={{ color: 'var(--crude-white-dim)', marginBottom: '0.5rem', fontSize: '1rem' }}>
          Want your company featured?
        </p>
        <a href="mailto:support@crudedesigns.ca?subject=Company Feature Request" style={{ color: 'var(--crude-accent)', textDecoration: 'none', fontSize: '0.9rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Contact us →
        </a>
      </div>
    </section>
  );
};

// Hard Hat Rating Component (1-5 hard hats)
const StarRating = ({ rating, onRate, readonly = false, size = 20 }) => {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: '0.25rem' }}>
      {[1, 2, 3, 4, 5].map(hat => {
        const active = (hover || rating) >= hat;
        return (
          <HardHat
            key={hat}
            size={size}
            fill={active ? 'var(--crude-accent)' : 'none'}
            color={active ? 'var(--crude-accent)' : 'var(--crude-white-dim)'}
            style={{ cursor: readonly ? 'default' : 'pointer', transition: 'all 0.15s' }}
            onMouseEnter={() => !readonly && setHover(hat)}
            onMouseLeave={() => !readonly && setHover(0)}
            onClick={() => !readonly && onRate && onRate(hat)}
            data-testid={`hardhat-${hat}`}
          />
        );
      })}
    </div>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, name });
      setMessage('Thanks for joining! Check your inbox for updates.');
      setEmail('');
      setName('');
    } catch {
      setMessage('Something went wrong. Try again.');
    }
    setLoading(false);
  };

  return (
    <section className="section" style={{ textAlign: 'center', maxWidth: '600px' }}>
      <div className="section-header">
        <h2 className="section-title">JOIN THE CREW</h2>
        <p className="section-subtitle">Get first access to new drops, restocks, and exclusive gear.</p>
      </div>
      <form onSubmit={handleSubscribe} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '400px', margin: '0 auto' }}>
        <input
          type="text"
          placeholder="Your name (optional)"
          className="form-input"
          value={name}
          onChange={e => setName(e.target.value)}
          data-testid="newsletter-name"
        />
        <input
          type="email"
          placeholder="Your email"
          className="form-input"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          data-testid="newsletter-email"
        />
        <button type="submit" className="btn btn-primary" disabled={loading} data-testid="newsletter-submit">
          {loading ? 'Subscribing...' : 'Subscribe'}
        </button>
      </form>
      {message && <p style={{ marginTop: '1rem', color: 'var(--crude-accent)' }}>{message}</p>}
    </section>
  );
};

// Reviews Section
const ReviewsSection = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/${productId}`);
      setReviews(data.reviews);
      setAvgRating(data.average_rating);
    } catch {
      setReviews([]);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      alert('Please select a rating');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { product_id: productId, rating, name, comment });
      setShowForm(false);
      setRating(0);
      setName('');
      setComment('');
      fetchReviews();
    } catch {
      alert('Failed to submit review');
    }
    setSubmitting(false);
  };

  return (
    <div style={{ marginTop: '3rem', padding: '2rem 0', borderTop: '1px solid var(--crude-gray)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h3 style={{ fontSize: '1.5rem', margin: 0, letterSpacing: '0.1em' }}>REVIEWS</h3>
          {reviews.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
              <StarRating rating={Math.round(avgRating)} readonly size={16} />
              <span style={{ color: 'var(--crude-white-dim)', fontSize: '0.9rem' }}>
                {avgRating} ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
              </span>
            </div>
          )}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(!showForm)} data-testid="write-review-btn">
          {showForm ? 'Cancel' : 'Write a Review'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', padding: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label">Your Rating (1-5 Hard Hats)</label>
            <StarRating rating={rating} onRate={setRating} size={28} />
          </div>
          <div className="form-group">
            <label className="form-label">Your Name</label>
            <input type="text" className="form-input" value={name} onChange={e => setName(e.target.value)} required data-testid="review-name" />
          </div>
          <div className="form-group">
            <label className="form-label">Your Review</label>
            <textarea className="form-input" rows={3} value={comment} onChange={e => setComment(e.target.value)} required data-testid="review-comment" />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="submit-review">
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </form>
      )}

      <div>
        {reviews.length === 0 ? (
          <p style={{ color: 'var(--crude-white-dim)', textAlign: 'center', padding: '2rem' }}>
            No reviews yet. Be the first to review this hat!
          </p>
        ) : (
          reviews.map(review => (
            <div key={review.id} style={{ padding: '1.5rem 0', borderBottom: '1px solid var(--crude-gray)' }} data-testid={`review-${review.id}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <strong>{review.name}</strong>
                <span style={{ color: 'var(--crude-white-dim)', fontSize: '0.85rem' }}>
                  {new Date(review.created_at).toLocaleDateString()}
                </span>
              </div>
              <StarRating rating={review.rating} readonly size={14} />
              <p style={{ marginTop: '0.75rem', color: 'var(--crude-white-dim)', lineHeight: 1.6 }}>{review.comment}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Home Page
const Home = () => {
  const [featured, setFeatured] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products?featured=true`).then(r => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div className="fade-in">
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg" style={{ 
          backgroundImage: "url('https://customer-assets-39nsmqrw.emergentagent.net/job_alberta-crude-launch/artifacts/mm4yn85r_IMG_3099.webp')",
          opacity: 0.35,
          filter: 'grayscale(20%)'
        }} />
        <div className="hero-content">
          <span className="hero-badge">Built in Alberta</span>
          <h1 className="hero-title">CRUDE<br/><span style={{ fontSize: '0.4em', letterSpacing: '0.3em' }}>DESIGNS</span></h1>
          <p className="hero-subtitle">Premium apparel for rig hands, welders, operators, and anyone who works hard.</p>
          <div className="hero-cta">
            <Link to="/shop" className="btn btn-primary" data-testid="hero-shop-btn">Shop Now</Link>
            <Link to="/shop?category=hats" className="btn btn-secondary">View Hats</Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">FEATURED DROPS</h2>
          <p className="section-subtitle">Our most popular pieces. Built tough, designed clean.</p>
        </div>
        <div className="product-grid">
          {featured.map(product => (
            <div 
              key={product.id} 
              className="product-card" 
              onClick={() => navigate(`/product/${product.id}`)}
              data-testid={`product-card-${product.id}`}
            >
              <div className="product-image">
                <img src={product.images?.[0] || '/placeholder.jpg'} alt={product.name} />
              </div>
              <div className="product-info">
                <span className="product-category">{product.category}</span>
                <h3 className="product-name">{product.name}</h3>
                <p className="product-price">${product.price.toFixed(2)} CAD</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Collection Showcase - Full Lineup */}
      <section style={{ padding: '0', position: 'relative', margin: '4rem 0' }}>
        <div style={{ 
          height: '500px', 
          backgroundImage: "url('https://customer-assets-39nsmqrw.emergentagent.net/job_alberta-crude-launch/artifacts/9rjtkdg7_IMG_3100.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.85) 100%)' }} />
          <div style={{ position: 'relative', textAlign: 'center', zIndex: 1, padding: '2rem' }}>
            <h2 className="section-title" style={{ fontSize: '3rem', margin: '0 0 1rem' }}>THE FULL LINEUP</h2>
            <p style={{ color: 'var(--crude-white-dim)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
              Four hats. Two colors. All built for the grind. Named after the Alberta towns that built this province.
            </p>
            <Link to="/shop" className="btn btn-primary" data-testid="lineup-shop-btn">Shop The Collection</Link>
          </div>
        </div>
      </section>

      {/* The Crew - Companies Who Rep CRUDE */}
      <CompaniesSection />

      {/* Newsletter Signup */}
      <NewsletterSection />

      {/* Brand Story */}
      <section className="section" style={{ textAlign: 'center', maxWidth: '800px' }}>
        <div className="section-header">
          <h2 className="section-title">BUILT ON GRIT</h2>
          <p className="section-subtitle" style={{ maxWidth: '700px' }}>
            CRUDE Designs™ is a lifestyle brand for rig hands, welders, operators, and anyone proud to call Alberta home. 
            We blend oilfield heritage, Western style, and modern streetwear into clean, everyday gear.
            From lease roads to city nights — our hats and hoodies go wherever you do.
          </p>
        </div>
        <Link to="/shop" className="btn btn-secondary">Explore Collection</Link>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Shop Page
const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const category = searchParams.get('category');

  useEffect(() => {
    setLoading(true);
    const url = category ? `${API}/products?category=${category}` : `${API}/products`;
    axios.get(url).then(r => {
      setProducts(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [category]);

  const categories = ['all', 'hats'];

  return (
    <div className="fade-in" style={{ paddingTop: '100px', minHeight: '100vh' }}>
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">{category ? category.toUpperCase() : 'ALL PRODUCTS'}</h2>
        </div>

        <div className="category-filter">
          {categories.map(cat => (
            <button
              key={cat}
              className={`category-btn ${(!category && cat === 'all') || category === cat ? 'active' : ''}`}
              onClick={() => cat === 'all' ? setSearchParams({}) : setSearchParams({ category: cat })}
              data-testid={`filter-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <div className="product-grid">
            {products.map(product => (
              <div 
                key={product.id} 
                className="product-card" 
                onClick={() => navigate(`/product/${product.id}`)}
                data-testid={`product-card-${product.id}`}
              >
                <div className="product-image" style={{ position: 'relative' }}>
                  <img src={product.images?.[0] || '/placeholder.jpg'} alt={product.name} />
                  {product.stock === 0 && (
                    <div style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'var(--crude-red)', color: 'white', padding: '0.35rem 0.75rem', fontSize: '0.7rem', letterSpacing: '0.15em', fontWeight: 600 }}>
                      SOLD OUT
                    </div>
                  )}
                  {product.stock > 0 && product.stock <= 10 && (
                    <div style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'var(--crude-accent)', color: 'var(--crude-black)', padding: '0.35rem 0.75rem', fontSize: '0.7rem', letterSpacing: '0.15em', fontWeight: 600 }}>
                      LOW STOCK
                    </div>
                  )}
                </div>
                <div className="product-info">
                  <span className="product-category">{product.category}</span>
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-price">${product.price.toFixed(2)} CAD</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
};

// Product Detail Page
const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [added, setAdded] = useState(false);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(r => {
      setProduct(r.data);
      if (r.data.variants?.length > 0) {
        setSelectedVariant(r.data.variants[0]);
      }
      setLoading(false);
      // Track product view click
      axios.post(`${API}/analytics/product-click`, { product_id: id }).catch(() => {});
    }).catch(() => {
      setLoading(false);
      navigate('/shop');
    });
  }, [id, navigate]);

  const handleAddToCart = async () => {
    await addToCart(product.id, quantity, selectedVariant);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (loading) return <div className="product-detail"><div className="loading"><div className="spinner" /></div></div>;
  if (!product) return null;

  const variantColors = [...new Set(product.variants?.map(v => v.color) || [])];
  const variantSizes = [...new Set(product.variants?.map(v => v.size).filter(Boolean) || [])];

  return (
    <div className="product-detail fade-in">
      <div className="product-detail-grid">
        <div className="product-gallery">
          <button 
            onClick={() => navigate('/shop')} 
            style={{ background: 'none', border: 'none', color: 'var(--crude-white-dim)', cursor: 'pointer', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <ChevronLeft size={20} /> Back to Shop
          </button>
          <div className="product-main-image">
            <img 
              src={
                selectedVariant?.color === 'White' && product.images?.[1]
                  ? product.images[1]
                  : product.images?.[0] || '/placeholder.jpg'
              } 
              alt={product.name} 
            />
          </div>
          {product.images?.length > 1 && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              {product.images.map((img, i) => (
                <div key={i} style={{ width: '80px', height: '80px', border: '1px solid var(--crude-gray)', overflow: 'hidden', cursor: 'pointer' }}>
                  <img src={img} alt={`View ${i+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="product-detail-info">
          <span className="product-detail-category">{product.category}</span>
          <h1 className="product-detail-title" data-testid="product-title">{product.name}</h1>
          <p className="product-detail-price" data-testid="product-price">${product.price.toFixed(2)} CAD</p>
          <p className="product-description">{product.description}</p>

          {variantColors.length > 0 && (
            <div className="variant-selector">
              <span className="variant-label">Color</span>
              <div className="variant-options">
                {variantColors.map(color => (
                  <button
                    key={color}
                    className={`variant-option ${selectedVariant?.color === color ? 'selected' : ''}`}
                    onClick={() => setSelectedVariant(prev => ({ ...prev, color }))}
                    data-testid={`variant-color-${color}`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}

          {variantSizes.length > 0 && (
            <div className="variant-selector">
              <span className="variant-label">Size</span>
              <div className="variant-options">
                {variantSizes.map(size => (
                  <button
                    key={size}
                    className={`variant-option ${selectedVariant?.size === size ? 'selected' : ''}`}
                    onClick={() => setSelectedVariant(prev => ({ ...prev, size }))}
                    data-testid={`variant-size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="quantity-selector">
            <span className="variant-label">Quantity</span>
            <button className="quantity-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))} data-testid="qty-minus">
              <Minus size={16} />
            </button>
            <span className="quantity-value" data-testid="qty-value">{quantity}</span>
            <button className="quantity-btn" onClick={() => setQuantity(q => q + 1)} data-testid="qty-plus">
              <Plus size={16} />
            </button>
          </div>

          {product.stock > 0 && product.stock <= 10 && (
            <p style={{ color: 'var(--crude-accent)', fontSize: '0.85rem', marginBottom: '1rem', letterSpacing: '0.05em' }}>
              ⚠ Only {product.stock} left in stock
            </p>
          )}
          {product.stock === 0 ? (
            <button 
              className="btn btn-primary add-to-cart-btn"
              disabled
              data-testid="out-of-stock-btn"
              style={{ background: 'var(--crude-gray)', color: 'var(--crude-white-dim)', cursor: 'not-allowed' }}
            >
              Sold Out
            </button>
          ) : (
            <button 
              className={`btn btn-primary add-to-cart-btn ${added ? 'btn-success' : ''}`}
              onClick={handleAddToCart}
              data-testid="add-to-cart-btn"
              style={added ? { background: '#4caf50' } : {}}
            >
              {added ? <><Check size={18} /> Added to Cart</> : 'Add to Cart'}
            </button>
          )}
        </div>
      </div>
      
      {/* Reviews Section */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2rem' }}>
        <ReviewsSection productId={product.id} />
      </div>
      
      <Footer />
    </div>
  );
};

// Cart Page
const Cart = () => {
  const { cart, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();

  if (cart.items.length === 0) {
    return (
      <div className="cart-page fade-in">
        <div className="cart-container">
          <div className="cart-empty">
            <ShoppingBag size={64} style={{ color: 'var(--crude-gray-light)', marginBottom: '1rem' }} />
            <h2 className="cart-empty-title">Your cart is empty</h2>
            <p style={{ color: 'var(--crude-white-dim)', marginBottom: '2rem' }}>Looks like you haven't added anything yet.</p>
            <Link to="/shop" className="btn btn-primary">Start Shopping</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="cart-page fade-in">
      <div className="cart-container">
        <h1 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Your Cart</h1>
        
        <div className="cart-items">
          {cart.items.map(item => (
            <div key={item.id} className="cart-item" data-testid={`cart-item-${item.id}`}>
              <div className="cart-item-image">
                <img src={item.product.images?.[0] || '/placeholder.jpg'} alt={item.product.name} data-testid={`cart-item-image-${item.id}`} />
              </div>
              <div className="cart-item-details">
                <h3 className="cart-item-name" data-testid={`cart-item-name-${item.id}`}>{item.product.name}</h3>
                {item.variant && (
                  <p className="cart-item-variant" data-testid={`cart-item-variant-${item.id}`}>
                    {item.variant.color && `Color: ${item.variant.color}`}
                    {item.variant.size && ` / Size: ${item.variant.size}`}
                  </p>
                )}
                <div className="cart-item-actions">
                  <button className="quantity-btn" onClick={() => updateQuantity(item.id, item.quantity - 1)} data-testid={`cart-qty-minus-${item.id}`}>
                    <Minus size={14} />
                  </button>
                  <span data-testid={`cart-qty-value-${item.id}`}>{item.quantity}</span>
                  <button className="quantity-btn" onClick={() => updateQuantity(item.id, item.quantity + 1)} data-testid={`cart-qty-plus-${item.id}`}>
                    <Plus size={14} />
                  </button>
                  <button 
                    className="quantity-btn" 
                    style={{ marginLeft: '1rem', color: 'var(--crude-red)' }}
                    onClick={() => removeFromCart(item.id)}
                    data-testid={`remove-item-${item.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="cart-item-price">${item.item_total.toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="cart-summary">
          <div className="cart-summary-row">
            <span>Subtotal</span>
            <span>${cart.total.toFixed(2)}</span>
          </div>
          <div className="cart-summary-row">
            <span>Shipping</span>
            <span>$10.00</span>
          </div>
          <div className="cart-summary-row total">
            <span>Total</span>
            <span>${(cart.total + 10).toFixed(2)} CAD</span>
          </div>
          <button 
            className="btn btn-primary" 
            style={{ width: '100%' }}
            onClick={() => navigate('/checkout')}
            data-testid="checkout-btn"
          >
            Proceed to Checkout
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Checkout Page
const Checkout = () => {
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    province: 'AB',
    postalCode: '',
    phone: ''
  });

  useEffect(() => {
    if (cart.items.length === 0) {
      navigate('/cart');
    }
  }, [cart, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data } = await axios.post(`${API}/checkout/create-session`, {
        origin_url: window.location.origin,
        cart_items: cart.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          variant: item.variant
        })),
        email: formData.email,
        shipping_address: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          address: formData.address,
          city: formData.city,
          province: formData.province,
          postalCode: formData.postalCode,
          phone: formData.phone,
          country: 'Canada'
        }
      });

      // Navigate to branded success page which auto-redirects to PayPal after 3s
      clearCart().catch(() => {});
      navigate(`/checkout/success?order_id=${data.order_id}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="checkout-page fade-in">
      <div className="checkout-container">
        <button 
          onClick={() => navigate('/cart')} 
          style={{ background: 'none', border: 'none', color: 'var(--crude-white-dim)', cursor: 'pointer', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <ChevronLeft size={20} /> Back to Cart
        </button>
        <h1 className="checkout-title">Checkout</h1>

        {error && <div className="auth-error"><AlertCircle size={16} style={{ marginRight: '0.5rem' }} />{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input 
              type="email" 
              name="email" 
              className="form-input" 
              value={formData.email}
              onChange={handleChange}
              required
              data-testid="checkout-email"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input 
                type="text" 
                name="firstName" 
                className="form-input" 
                value={formData.firstName}
                onChange={handleChange}
                required
                data-testid="checkout-first-name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input 
                type="text" 
                name="lastName" 
                className="form-input" 
                value={formData.lastName}
                onChange={handleChange}
                required
                data-testid="checkout-last-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <input 
              type="text" 
              name="address" 
              className="form-input" 
              value={formData.address}
              onChange={handleChange}
              required
              data-testid="checkout-address"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">City</label>
              <input 
                type="text" 
                name="city" 
                className="form-input" 
                value={formData.city}
                onChange={handleChange}
                required
                data-testid="checkout-city"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Province</label>
              <select 
                name="province" 
                className="form-input" 
                value={formData.province}
                onChange={handleChange}
                data-testid="checkout-province"
              >
                <option value="AB">Alberta</option>
                <option value="BC">British Columbia</option>
                <option value="SK">Saskatchewan</option>
                <option value="MB">Manitoba</option>
                <option value="ON">Ontario</option>
                <option value="QC">Quebec</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Postal Code</label>
              <input 
                type="text" 
                name="postalCode" 
                className="form-input" 
                value={formData.postalCode}
                onChange={handleChange}
                required
                data-testid="checkout-postal-code"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input 
                type="tel" 
                name="phone" 
                className="form-input" 
                value={formData.phone}
                onChange={handleChange}
                data-testid="checkout-phone"
              />
            </div>
          </div>

          <div style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>Subtotal</span>
              <span>${cart.total.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>Shipping</span>
              <span>$10.00</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.25rem', paddingTop: '1rem', borderTop: '1px solid var(--crude-gray)' }}>
              <span>Total</span>
              <span>${(cart.total + 10).toFixed(2)} CAD</span>
            </div>
          </div>

          <div style={{ background: 'rgba(0, 112, 186, 0.1)', border: '2px solid #0070ba', padding: '1.25rem', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0070ba' }}>P</div>
              <strong style={{ color: '#0070ba', letterSpacing: '0.1em' }}>SECURE PAYMENT VIA PAYPAL</strong>
            </div>
            <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.6, color: 'var(--crude-white-dim)' }}>
              Pay securely with PayPal balance, credit card, or bank. Your order ships within 24-48 hours of payment received.
            </p>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{ width: '100%', background: '#0070ba', color: 'white' }}
            disabled={loading}
            data-testid="pay-btn"
          >
            {loading ? 'Processing...' : `Pay $${(cart.total + 10).toFixed(2)} with PayPal →`}
          </button>
        </form>
      </div>
    </div>
  );
};

// Checkout Success - PayPal-first with auto-redirect + e-Transfer fallback
const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(3);
  const [redirected, setRedirected] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    const orderId = searchParams.get('order_id');
    if (!orderId) {
      setLoading(false);
      return;
    }
    axios.get(`${API}/checkout/status/${orderId}`).then(r => {
      setOrder(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [searchParams]);

  // Auto-redirect to PayPal after 3s
  useEffect(() => {
    if (!order?.paypal_me || redirected) return;
    if (countdown <= 0) {
      setRedirected(true);
      window.open(order.paypal_me, '_blank', 'noopener,noreferrer');
      return;
    }
    const t = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, order, redirected]);

  const openPayPalNow = () => {
    if (!order?.paypal_me) return;
    setRedirected(true);
    window.open(order.paypal_me, '_blank', 'noopener,noreferrer');
  };

  if (loading) return <div className="result-page"><div className="spinner" /></div>;

  // If order lookup failed (no order returned), show error instead of fake success
  if (!order || !order.order_number) {
    return (
      <div className="fade-in" style={{ paddingTop: '100px', minHeight: '100vh' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem', textAlign: 'center' }}>
          <div style={{ color: '#e53935', marginBottom: '1rem' }}><AlertCircle size={64} style={{ margin: '0 auto' }} /></div>
          <h1 style={{ fontSize: '2rem', margin: '0 0 0.5rem' }} data-testid="checkout-error-title">Order Not Found</h1>
          <p style={{ color: 'var(--crude-white-dim)', marginBottom: '2rem' }}>
            We couldn&apos;t load this order. It may have expired or the link is invalid.
          </p>
          <Link to="/shop" className="btn btn-secondary">Back to Shop</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ paddingTop: '100px', minHeight: '100vh' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ color: '#4caf50', marginBottom: '1rem' }}><Check size={64} style={{ margin: '0 auto' }} /></div>
          <h1 style={{ fontSize: '2rem', margin: '0 0 0.5rem' }}>Order Placed!</h1>
          {order?.order_number && (
            <p style={{ color: 'var(--crude-white-dim)' }}>Order #: <strong style={{ color: 'var(--crude-accent)' }} data-testid="order-number">{order.order_number}</strong></p>
          )}
        </div>

        {/* PayPal primary CTA */}
        <div style={{ background: 'var(--crude-charcoal)', border: '2px solid var(--crude-accent)', padding: '2rem', marginBottom: '1.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '0.75rem', letterSpacing: '0.2em', color: 'var(--crude-white-dim)', marginBottom: '0.5rem' }}>AMOUNT DUE</div>
            <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--crude-accent)' }} data-testid="paypal-amount">
              ${order?.total?.toFixed(2) || '0.00'} <span style={{ fontSize: '1rem' }}>CAD</span>
            </div>
          </div>

          {!redirected && countdown > 0 && (
            <div style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--crude-white-dim)' }} data-testid="paypal-countdown">
              Opening PayPal in <strong style={{ color: 'var(--crude-accent)', fontSize: '1.1rem' }}>{countdown}</strong>...
            </div>
          )}

          {order?.paypal_me && (
            <button
              type="button"
              onClick={openPayPalNow}
              className="btn"
              style={{ width: '100%', background: '#0070ba', color: 'white', fontSize: '1rem', padding: '1rem', border: 'none', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.1em' }}
              data-testid="paypal-pay-btn"
            >
              {redirected ? 'REOPEN PAYPAL' : `PAY $${order?.total?.toFixed(2)} WITH PAYPAL →`}
            </button>
          )}

          {redirected && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(76, 175, 80, 0.1)', border: '1px solid #4caf50', fontSize: '0.85rem', color: 'var(--crude-white)', textAlign: 'center' }} data-testid="paypal-opened-msg">
              PayPal opened in a new tab. Complete your payment there, then return here.
            </div>
          )}

          <div style={{ padding: '1rem', marginTop: '1rem', background: 'rgba(212, 168, 83, 0.08)', border: '1px solid var(--crude-gray)', fontSize: '0.8rem', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--crude-accent)' }}>Payment steps:</strong>
            <ol style={{ margin: '0.5rem 0 0 1.25rem', padding: 0, color: 'var(--crude-white-dim)' }}>
              <li>Sign in or check out as guest on PayPal</li>
              <li>Confirm the amount and complete payment</li>
              <li>Add order # <strong style={{ color: 'var(--crude-white)' }}>{order?.order_number}</strong> in the note field</li>
            </ol>
          </div>
        </div>

        {/* e-Transfer fallback */}
        <div style={{ marginBottom: '1.5rem' }}>
          <button
            type="button"
            onClick={() => setShowFallback(!showFallback)}
            data-testid="toggle-etransfer"
            style={{ width: '100%', padding: '0.75rem', background: 'transparent', border: '1px solid var(--crude-gray)', color: 'var(--crude-white-dim)', fontSize: '0.8rem', letterSpacing: '0.1em', cursor: 'pointer', textTransform: 'uppercase' }}
          >
            {showFallback ? '− Hide' : '+ Prefer'} Interac e-Transfer instead?
          </button>

          {showFallback && (
            <div style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', borderTop: 'none', padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', letterSpacing: '0.15em', color: 'var(--crude-white-dim)', marginBottom: '0.5rem' }}>SEND TO:</div>
                <div style={{ background: 'var(--crude-black)', border: '1px solid var(--crude-gray)', padding: '0.85rem', fontSize: '1rem', fontWeight: 600, wordBreak: 'break-all', userSelect: 'all' }} data-testid="etransfer-email">
                  {order?.etransfer_email || 'CRUDEDesigns2026@outlook.com'}
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', letterSpacing: '0.15em', color: 'var(--crude-white-dim)', marginBottom: '0.5rem' }}>AMOUNT & MEMO:</div>
                <div style={{ background: 'var(--crude-black)', border: '1px solid var(--crude-gray)', padding: '0.85rem', fontSize: '0.95rem', userSelect: 'all' }} data-testid="etransfer-memo">
                  ${order?.total?.toFixed(2)} CAD — Order {order?.order_number}
                </div>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--crude-white-dim)', margin: 0 }}>
                Include the order number in the message field so we can match your payment.
              </p>
            </div>
          )}
        </div>

        <div style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', padding: '1.5rem', marginBottom: '1.5rem', fontSize: '0.9rem', lineHeight: 1.6, color: 'var(--crude-white-dim)' }}>
          <strong style={{ color: 'var(--crude-white)' }}>What happens next?</strong>
          <ul style={{ margin: '0.5rem 0 0 1.25rem', padding: 0 }}>
            <li>We verify your payment (usually within a few hours)</li>
            <li>You'll get an email confirmation once received</li>
            <li>Your order ships within 24-48 hours after payment</li>
            <li>Questions? Email <a href="mailto:support@crudedesigns.ca" style={{ color: 'var(--crude-accent)' }}>support@crudedesigns.ca</a></li>
          </ul>
        </div>

        <div style={{ textAlign: 'center' }}>
          <Link to="/shop" className="btn btn-secondary">Continue Shopping</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Checkout Cancel
const CheckoutCancel = () => {
  return (
    <div className="result-page fade-in">
      <div>
        <div className="result-icon" style={{ color: 'var(--crude-accent)' }}><X size={64} /></div>
        <h1 className="result-title">Checkout Cancelled</h1>
        <p className="result-message">Your order was not completed. Your cart items are still saved.</p>
        <Link to="/cart" className="btn btn-primary">Return to Cart</Link>
      </div>
    </div>
  );
};

// Login Page
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(user.role === 'admin' ? '/admin' : '/');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await login(email, password);
      navigate(data.role === 'admin' ? '/admin' : '/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid credentials');
      setLoading(false);
    }
  };

  return (
    <div className="auth-page fade-in">
      <div className="auth-card">
        <h1 className="auth-title">CRUDE Designs™ Login</h1>
        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input 
              type="email" 
              className="form-input" 
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              data-testid="login-email"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input 
              type="password" 
              className="form-input" 
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              data-testid="login-password"
            />
          </div>
          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{ width: '100%' }}
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  if (authLoading || !user) return <div className="loading"><div className="spinner" /></div>;

  const currentPath = location.pathname;

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <Link to="/admin" className={`admin-nav-item ${currentPath === '/admin' ? 'active' : ''}`}>
          <BarChart3 size={18} /> Dashboard
        </Link>
        <Link to="/admin/products" className={`admin-nav-item ${currentPath.includes('/products') ? 'active' : ''}`}>
          <Package size={18} /> Products
        </Link>
        <Link to="/admin/orders" className={`admin-nav-item ${currentPath.includes('/orders') ? 'active' : ''}`}>
          <ShoppingBag size={18} /> Orders
        </Link>
        <Link to="/admin/analytics" className={`admin-nav-item ${currentPath.includes('/analytics') ? 'active' : ''}`}>
          <TrendingUp size={18} /> Analytics
        </Link>
        <Link to="/admin/subscribers" className={`admin-nav-item ${currentPath.includes('/subscribers') ? 'active' : ''}`}>
          <Mail size={18} /> Subscribers
        </Link>
        <Link to="/admin/companies" className={`admin-nav-item ${currentPath.includes('/companies') ? 'active' : ''}`}>
          <Users size={18} /> Companies
        </Link>
      </aside>
      <main className="admin-content">
        <Routes>
          <Route index element={<AdminOverview />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="subscribers" element={<AdminSubscribers />} />
          <Route path="companies" element={<AdminCompanies />} />
        </Routes>
      </main>
    </div>
  );
};

// Admin Analytics
const AdminAnalytics = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/analytics`).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="admin-header">
        <h1 className="admin-title">Analytics</h1>
      </div>
      <div className="stats-grid">
        <div className="stat-card">
          <p className="stat-label">Total Product Clicks</p>
          <p className="stat-value" data-testid="total-clicks">{data.total_clicks}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Email Subscribers</p>
          <p className="stat-value">{data.total_subscribers}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Total Reviews</p>
          <p className="stat-value">{data.total_reviews}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Top Product</p>
          <p className="stat-value" style={{ fontSize: '1.25rem' }}>{data.product_clicks?.[0]?.product_name || 'N/A'}</p>
        </div>
      </div>

      <h2 style={{ marginTop: '2rem', marginBottom: '1rem', fontSize: '1.25rem', letterSpacing: '0.1em' }}>PRODUCT CLICKS</h2>
      <table className="data-table" data-testid="analytics-table">
        <thead>
          <tr>
            <th>Rank</th>
            <th>Product</th>
            <th>Clicks</th>
          </tr>
        </thead>
        <tbody>
          {data.product_clicks?.length === 0 ? (
            <tr><td colSpan="3" style={{ textAlign: 'center', padding: '2rem', color: 'var(--crude-white-dim)' }}>No clicks tracked yet</td></tr>
          ) : (
            data.product_clicks?.map((p, i) => (
              <tr key={p.product_id}>
                <td>#{i + 1}</td>
                <td>{p.product_name}</td>
                <td>{p.clicks}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// Admin Subscribers
const AdminSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/newsletter-subscribers`).then(r => {
      setSubscribers(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const exportCSV = () => {
    const csv = 'Name,Email,Subscribed\n' + subscribers.map(s => `"${s.name || ''}","${s.email}","${s.subscribed_at}"`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subscribers.csv';
    a.click();
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="admin-header">
        <h1 className="admin-title">Email Subscribers ({subscribers.length})</h1>
        <button className="btn btn-secondary btn-sm" onClick={exportCSV} data-testid="export-subscribers">
          Export CSV
        </button>
      </div>
      <table className="data-table" data-testid="subscribers-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Subscribed</th>
          </tr>
        </thead>
        <tbody>
          {subscribers.length === 0 ? (
            <tr><td colSpan="3" style={{ textAlign: 'center', padding: '2rem', color: 'var(--crude-white-dim)' }}>No subscribers yet</td></tr>
          ) : (
            subscribers.map(s => (
              <tr key={s.id}>
                <td>{s.name || '—'}</td>
                <td>{s.email}</td>
                <td>{new Date(s.subscribed_at).toLocaleDateString()}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// Admin Companies
const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', logo_url: '', location: '', industry: '' });

  const fetchCompanies = async () => {
    const { data } = await axios.get(`${API}/companies`);
    setCompanies(data);
  };

  useEffect(() => { fetchCompanies(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/companies`, formData);
    setFormData({ name: '', logo_url: '', location: '', industry: '' });
    setShowForm(false);
    fetchCompanies();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Remove this company?')) {
      await axios.delete(`${API}/companies/${id}`);
      fetchCompanies();
    }
  };

  return (
    <div className="fade-in">
      <div className="admin-header">
        <h1 className="admin-title">Companies Who Rep CRUDE ({companies.length})</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)} data-testid="add-company-btn">
          {showForm ? 'Cancel' : '+ Add Company'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', padding: '1.5rem', marginBottom: '2rem' }}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input className="form-input" value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} required data-testid="company-name" />
            </div>
            <div className="form-group">
              <label className="form-label">Industry</label>
              <input className="form-input" placeholder="Oilfield Services, Trucking..." value={formData.industry} onChange={e => setFormData(p => ({...p, industry: e.target.value}))} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-input" placeholder="Calgary, AB" value={formData.location} onChange={e => setFormData(p => ({...p, location: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Logo URL (optional)</label>
              <input className="form-input" placeholder="https://..." value={formData.logo_url} onChange={e => setFormData(p => ({...p, logo_url: e.target.value}))} />
            </div>
          </div>
          <button type="submit" className="btn btn-primary">Add Company</button>
        </form>
      )}

      <table className="data-table" data-testid="companies-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Industry</th>
            <th>Location</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {companies.length === 0 ? (
            <tr><td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: 'var(--crude-white-dim)' }}>No companies added yet</td></tr>
          ) : (
            companies.map(c => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.industry || '—'}</td>
                <td>{c.location || '—'}</td>
                <td>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id)} data-testid={`delete-company-${c.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// Admin Overview
const AdminOverview = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="admin-header">
        <h1 className="admin-title">Dashboard</h1>
      </div>
      <div className="stats-grid">
        <div className="stat-card" data-testid="stat-revenue">
          <p className="stat-label">Total Revenue</p>
          <p className="stat-value">${stats.total_revenue.toFixed(2)}</p>
        </div>
        <div className="stat-card" data-testid="stat-orders">
          <p className="stat-label">Total Orders</p>
          <p className="stat-value">{stats.total_orders}</p>
        </div>
        <div className="stat-card" data-testid="stat-products">
          <p className="stat-label">Products</p>
          <p className="stat-value">{stats.total_products}</p>
        </div>
        <div className="stat-card" data-testid="stat-pending">
          <p className="stat-label">Pending Orders</p>
          <p className="stat-value">{stats.pending_orders}</p>
        </div>
      </div>
    </div>
  );
};

// Admin Products
const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null);
  const [formData, setFormData] = useState({});

  const fetchProducts = async () => {
    const { data } = await axios.get(`${API}/products`);
    setProducts(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const openEdit = (product) => {
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      stock: product.stock,
      featured: product.featured
    });
    setEditModal(product.id);
  };

  const handleSave = async () => {
    await axios.put(`${API}/products/${editModal}`, formData);
    setEditModal(null);
    fetchProducts();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      await axios.delete(`${API}/products/${id}`);
      fetchProducts();
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const handleReseed = async () => {
    if (window.confirm('This will DELETE all products and reseed with the 4 default hats. Continue?')) {
      try {
        const { data } = await axios.post(`${API}/admin/reseed-products`);
        alert(data.message);
        fetchProducts();
      } catch (err) {
        alert('Failed to reseed: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  return (
    <div className="fade-in">
      <div className="admin-header">
        <h1 className="admin-title">Products</h1>
        <button 
          className="btn btn-secondary btn-sm" 
          onClick={handleReseed}
          data-testid="reseed-btn"
          style={{ background: 'var(--crude-accent)', color: 'var(--crude-black)' }}
        >
          🔄 Reseed Products
        </button>
      </div>

      <table className="data-table" data-testid="products-table">
        <thead>
          <tr>
            <th>Product</th>
            <th>Category</th>
            <th>Price</th>
            <th>Stock</th>
            <th>Featured</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.map(product => (
            <tr key={product.id}>
              <td>{product.name}</td>
              <td>{product.category}</td>
              <td>${product.price.toFixed(2)}</td>
              <td>{product.stock}</td>
              <td>{product.featured ? 'Yes' : 'No'}</td>
              <td>
                <button 
                  className="btn btn-sm btn-secondary" 
                  style={{ marginRight: '0.5rem' }}
                  onClick={() => openEdit(product)}
                  data-testid={`edit-product-${product.id}`}
                >
                  <Edit size={14} />
                </button>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(product.id)}
                  data-testid={`delete-product-${product.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Product</h2>
              <button className="modal-close" onClick={() => setEditModal(null)}>&times;</button>
            </div>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input 
                className="form-input" 
                value={formData.name || ''}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea 
                className="form-input" 
                rows={3}
                value={formData.description || ''}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Price</label>
                <input 
                  type="number" 
                  step="0.01"
                  className="form-input" 
                  value={formData.price || ''}
                  onChange={e => setFormData(p => ({ ...p, price: parseFloat(e.target.value) }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Stock</label>
                <input 
                  type="number" 
                  className="form-input" 
                  value={formData.stock || ''}
                  onChange={e => setFormData(p => ({ ...p, stock: parseInt(e.target.value) }))}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select 
                className="form-input"
                value={formData.category || ''}
                onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}
              >
                <option value="hats">Hats</option>
                <option value="hoodies">Hoodies</option>
                <option value="tees">Tees</option>
                <option value="accessories">Accessories</option>
              </select>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={formData.featured || false}
                  onChange={e => setFormData(p => ({ ...p, featured: e.target.checked }))}
                />
                Featured Product
              </label>
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleSave}>
              Save Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Orders
const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchOrders = async () => {
    const { data } = await axios.get(`${API}/orders`);
    setOrders(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateStatus = async (orderId, status) => {
    await axios.put(`${API}/orders/${orderId}/status`, { status });
    fetchOrders();
  };

  const getStatusClass = (status) => {
    const classes = {
      'pending_payment': 'status-pending',
      'awaiting_payment': 'status-pending',
      'awaiting_etransfer': 'status-pending',
      'confirmed': 'status-confirmed',
      'processing': 'status-confirmed',
      'shipped': 'status-shipped',
      'delivered': 'status-delivered'
    };
    return classes[status] || 'status-pending';
  };

  const isPaid = (order) => {
    // Payment is confirmed once admin advances beyond the "awaiting" states
    return !['pending_payment', 'awaiting_payment', 'awaiting_etransfer'].includes(order.status);
  };

  const markPaid = async (orderId, e) => {
    e.stopPropagation();
    await axios.put(`${API}/orders/${orderId}/status`, { status: 'confirmed' });
    fetchOrders();
  };

  const formatAddress = (order) => {
    const a = order.shipping_address || {};
    return {
      recipient: `${a.firstName || ''} ${a.lastName || ''}`.trim() || '—',
      line1: a.address || '—',
      line2: `${a.city || ''}, ${a.province || ''} ${a.postalCode || ''}`.trim(),
      country: a.country || 'Canada',
      phone: a.phone || '—'
    };
  };

  const copyShippingLabel = (order) => {
    const a = formatAddress(order);
    const label = `${a.recipient}\n${a.line1}\n${a.line2}\n${a.country}\nPhone: ${a.phone}\nEmail: ${order.email}\nOrder: ${order.order_number}`;
    navigator.clipboard.writeText(label);
    alert('Shipping label copied to clipboard!');
  };

  const filteredOrders = orders;

  const unpaidCount = orders.filter(o => !isPaid(o)).length;
  const paidCount = orders.filter(o => isPaid(o)).length;

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="admin-header">
        <h1 className="admin-title">Orders ({filteredOrders.length})</h1>
        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem' }}>
          <span data-testid="unpaid-count" style={{ color: '#e53935', fontWeight: 700 }}>● {unpaidCount} UNPAID</span>
          <span data-testid="paid-count" style={{ color: '#4caf50', fontWeight: 700 }}>✓ {paidCount} PAID</span>
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--crude-white-dim)', background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)' }}>
          No orders yet. Orders will appear here after checkout.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredOrders.map(order => {
            const addr = formatAddress(order);
            const isExpanded = expanded === order.id;
            return (
              <div key={order.id} style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)' }} data-testid={`order-${order.id}`}>
                <div 
                  style={{ padding: '1.25rem', display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 0.8fr 0.9fr 1fr 1.2fr 0.8fr', gap: '1rem', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setExpanded(isExpanded ? null : order.id)}
                >
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--crude-white-dim)', letterSpacing: '0.1em' }}>ORDER</div>
                    <div style={{ fontWeight: 600 }}>{order.order_number}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--crude-white-dim)' }}>{new Date(order.created_at).toLocaleDateString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--crude-white-dim)', letterSpacing: '0.1em' }}>CUSTOMER</div>
                    <div style={{ fontWeight: 600 }}>{addr.recipient}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--crude-white-dim)' }}>{order.email}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--crude-white-dim)', letterSpacing: '0.1em' }}>ITEMS</div>
                    <div style={{ fontWeight: 600 }}>{order.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--crude-white-dim)', letterSpacing: '0.1em' }}>TOTAL</div>
                    <div style={{ fontWeight: 600 }}>${order.total?.toFixed(2) || '0.00'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--crude-white-dim)', letterSpacing: '0.1em' }}>PAYMENT</div>
                    {isPaid(order) ? (
                      <span
                        data-testid={`paid-badge-${order.id}`}
                        style={{ display: 'inline-block', padding: '0.25rem 0.6rem', background: 'rgba(76, 175, 80, 0.15)', border: '1px solid #4caf50', color: '#4caf50', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em' }}
                      >
                        ✓ PAID
                      </span>
                    ) : (
                      <span
                        data-testid={`unpaid-badge-${order.id}`}
                        style={{ display: 'inline-block', padding: '0.25rem 0.6rem', background: 'rgba(229, 57, 53, 0.15)', border: '1px solid #e53935', color: '#e53935', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em' }}
                      >
                        ● UNPAID
                      </span>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--crude-white-dim)', letterSpacing: '0.1em' }}>STATUS</div>
                    <span className={`status-badge ${getStatusClass(order.status)}`}>{order.status?.replace(/_/g, ' ')}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {!isPaid(order) && (
                      <button
                        className="btn btn-sm"
                        style={{ background: '#4caf50', color: 'white', border: 'none', padding: '0.4rem 0.75rem', fontSize: '0.75rem', letterSpacing: '0.1em', fontWeight: 700, cursor: 'pointer', marginBottom: '0.4rem', width: '100%' }}
                        onClick={(e) => markPaid(order.id, e)}
                        data-testid={`mark-paid-${order.id}`}
                        title="Confirm you received PayPal payment"
                      >
                        MARK PAID
                      </button>
                    )}
                    <div style={{ color: 'var(--crude-accent)', fontSize: '0.8rem' }}>
                      {isExpanded ? '▲ Hide' : '▼ Details'}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ borderTop: '1px solid var(--crude-gray)', padding: '1.5rem', background: 'var(--crude-black)' }}>
                    {/* PayPal Verification Panel - shown only if unpaid */}
                    {!isPaid(order) && (
                      <div style={{ marginBottom: '1.5rem', background: 'rgba(0, 112, 186, 0.08)', border: '2px solid #0070ba', padding: '1.25rem' }} data-testid={`paypal-verify-${order.id}`}>
                        <h4 style={{ fontSize: '0.85rem', letterSpacing: '0.15em', color: '#0070ba', margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          💰 VERIFY PAYPAL PAYMENT
                        </h4>
                        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'var(--crude-white-dim)', lineHeight: 1.5 }}>
                          Open your PayPal Activity and match a recent incoming payment with:
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                          <div style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', padding: '0.75rem' }}>
                            <div style={{ fontSize: '0.7rem', color: 'var(--crude-white-dim)', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>AMOUNT</div>
                            <div style={{ fontSize: '1.1rem', fontWeight: 700, userSelect: 'all' }}>${order.total?.toFixed(2)} CAD</div>
                          </div>
                          <div style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', padding: '0.75rem' }}>
                            <div style={{ fontSize: '0.7rem', color: 'var(--crude-white-dim)', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>MEMO / NOTE</div>
                            <div style={{ fontSize: '1.1rem', fontWeight: 700, userSelect: 'all' }}>{order.order_number}</div>
                          </div>
                          <div style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', padding: '0.75rem' }}>
                            <div style={{ fontSize: '0.7rem', color: 'var(--crude-white-dim)', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>SENDER EMAIL</div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, userSelect: 'all', wordBreak: 'break-all' }}>{order.email}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <a
                            href="https://www.paypal.com/myaccount/activity/all"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm"
                            style={{ background: '#0070ba', color: 'white', border: 'none', padding: '0.5rem 1rem', fontSize: '0.8rem', letterSpacing: '0.1em', fontWeight: 700, textDecoration: 'none' }}
                            data-testid={`open-paypal-${order.id}`}
                          >
                            OPEN PAYPAL ACTIVITY →
                          </a>
                          <button
                            className="btn btn-sm"
                            style={{ background: '#4caf50', color: 'white', border: 'none', padding: '0.5rem 1rem', fontSize: '0.8rem', letterSpacing: '0.1em', fontWeight: 700, cursor: 'pointer' }}
                            onClick={(e) => markPaid(order.id, e)}
                            data-testid={`confirm-paid-${order.id}`}
                          >
                            ✓ CONFIRM PAYMENT RECEIVED
                          </button>
                        </div>
                        <p style={{ margin: '1rem 0 0', fontSize: '0.75rem', color: 'var(--crude-white-dim)', lineHeight: 1.5 }}>
                          <strong>Tip:</strong> If the customer forgot to add the order # in the memo, match by amount + timestamp + sender email above.
                        </p>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                      {/* Shipping Address */}
                      <div>
                        <h4 style={{ fontSize: '0.85rem', letterSpacing: '0.15em', color: 'var(--crude-accent)', margin: '0 0 1rem' }}>📦 SHIP TO</h4>
                        <div style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', padding: '1rem', lineHeight: 1.8, fontSize: '0.95rem' }}>
                          <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{addr.recipient}</div>
                          <div>{addr.line1}</div>
                          <div>{addr.line2}</div>
                          <div>{addr.country}</div>
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--crude-white-dim)' }}>
                            📞 {addr.phone}<br />
                            ✉️ {order.email}
                          </div>
                        </div>
                        <button 
                          className="btn btn-secondary btn-sm" 
                          style={{ marginTop: '0.75rem', width: '100%' }} 
                          onClick={() => copyShippingLabel(order)}
                          data-testid={`copy-label-${order.id}`}
                        >
                          Copy Shipping Label
                        </button>
                      </div>

                      {/* Items */}
                      <div>
                        <h4 style={{ fontSize: '0.85rem', letterSpacing: '0.15em', color: 'var(--crude-accent)', margin: '0 0 1rem' }}>🧢 ITEMS TO SHIP</h4>
                        <div style={{ background: 'var(--crude-charcoal)', border: '1px solid var(--crude-gray)', padding: '1rem' }}>
                          {order.items?.map((item, i) => (
                            <div key={i} style={{ padding: '0.5rem 0', borderBottom: i < order.items.length - 1 ? '1px solid var(--crude-gray)' : 'none' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                                <span>{item.quantity}× {item.product_name}</span>
                                <span>${(item.price * item.quantity).toFixed(2)}</span>
                              </div>
                              {item.variant && (
                                <div style={{ fontSize: '0.8rem', color: 'var(--crude-white-dim)', marginTop: '0.25rem' }}>
                                  {item.variant.color && `Color: ${item.variant.color}`}
                                  {item.variant.size && ` · Size: ${item.variant.size}`}
                                  {item.variant.style && ` · ${item.variant.style}`}
                                </div>
                              )}
                            </div>
                          ))}
                          <div style={{ paddingTop: '0.75rem', marginTop: '0.75rem', borderTop: '1px solid var(--crude-gray-light)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--crude-white-dim)' }}>
                              <span>Subtotal:</span><span>${order.subtotal?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--crude-white-dim)' }}>
                              <span>Shipping:</span><span>${order.shipping?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: '0.5rem' }}>
                              <span>Total:</span><span>${order.total?.toFixed(2) || '0.00'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status Update */}
                    <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <label style={{ fontSize: '0.85rem', letterSpacing: '0.1em' }}>UPDATE STATUS:</label>
                      <select 
                        className="form-input" 
                        style={{ padding: '0.5rem', width: 'auto' }}
                        value={order.status}
                        onChange={e => updateStatus(order.id, e.target.value)}
                        data-testid={`order-status-${order.id}`}
                      >
                        <option value="awaiting_payment">🕒 Awaiting Payment (PayPal)</option>
                        <option value="awaiting_etransfer">🕒 Awaiting E-Transfer</option>
                        <option value="confirmed">✓ Payment Received - Ready to Ship</option>
                        <option value="processing">📦 Processing</option>
                        <option value="shipped">🚚 Shipped</option>
                        <option value="delivered">🏁 Delivered</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Footer Component
const Footer = () => (
  <footer className="footer">
    <div className="footer-content">
      <div>
        <div className="footer-brand">CRUDE Designs™</div>
        <p className="footer-tagline">
          Built in Alberta. Inspired by the people who keep this province moving.
          Premium apparel for operators, welders, and hardworking Canadians.
        </p>
      </div>
      <div>
        <h4 className="footer-title">Shop</h4>
        <ul className="footer-links">
          <li><Link to="/shop?category=hats">Hats</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="footer-title">Support</h4>
        <ul className="footer-links">
          <li><a href="mailto:support@crudedesigns.ca">Shipping & Returns</a></li>
          <li><a href="mailto:support@crudedesigns.ca">FAQ</a></li>
          <li><a href="mailto:support@crudedesigns.ca">Contact</a></li>
        </ul>
      </div>
      <div>
        <h4 className="footer-title">Connect</h4>
        <ul className="footer-links">
          <li><a href="https://instagram.com/CRUDEDesigns" target="_blank" rel="noopener noreferrer">Instagram</a></li>
          <li><a href="mailto:support@crudedesigns.ca">Email Us</a></li>
          <li><Link to="/reviews">Reviews</Link></li>
        </ul>
      </div>
    </div>
    <div className="footer-bottom">
      © {new Date().getFullYear()} CRUDE Designs™ · crudedesigns.ca · Designed in Alberta · All rights reserved.
    </div>
  </footer>
);

// Email Capture Modal (for abandoned cart)
const EmailCapturePrompt = () => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const { cart } = useCart();

  useEffect(() => {
    const handler = () => {
      if (localStorage.getItem('crude_email_captured') || sessionStorage.getItem('crude_email_dismissed')) return;
      setShow(true);
    };
    window.addEventListener('show-email-prompt', handler);
    return () => window.removeEventListener('show-email-prompt', handler);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/abandoned-cart`, {
        email,
        cart_items: cart.items.map(i => ({ product_id: i.product_id, name: i.product?.name, quantity: i.quantity, variant: i.variant })),
        cart_total: cart.total
      });
      // Also subscribe to newsletter
      await axios.post(`${API}/newsletter/subscribe`, { email }).catch(() => {});
      localStorage.setItem('crude_email_captured', email);
      setShow(false);
    } catch {
      localStorage.setItem('crude_email_captured', email);
      setShow(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('crude_email_dismissed', '1');
    setDismissed(true);
    setShow(false);
  };

  if (!show || dismissed) return null;

  return (
    <div className="modal-overlay" onClick={handleDismiss}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }} data-testid="email-capture-modal">
        <div className="modal-header">
          <h2 className="modal-title">DON'T LOSE YOUR CART</h2>
          <button className="modal-close" onClick={handleDismiss} data-testid="email-modal-close">&times;</button>
        </div>
        <p style={{ color: 'var(--crude-white-dim)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
          Drop your email and we'll save your cart. Plus, you'll be first to know about restocks and new drops.
        </p>
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            className="form-input"
            placeholder="you@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            data-testid="email-capture-input"
            style={{ marginBottom: '1rem' }}
          />
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} data-testid="email-capture-submit">
            Save My Cart
          </button>
          <button type="button" onClick={handleDismiss} style={{ width: '100%', background: 'none', border: 'none', color: 'var(--crude-white-dim)', marginTop: '0.75rem', cursor: 'pointer', fontSize: '0.85rem' }}>
            No thanks
          </button>
        </form>
      </div>
    </div>
  );
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <div className="App">
            <Navigation />
            <EmailCapturePrompt />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/cancel" element={<CheckoutCancel />} />
              <Route path="/reviews" element={<AllReviews />} />
              <Route path="/track-order" element={<TrackOrder />} />
              <Route path="/login" element={<Login />} />
              <Route path="/admin/*" element={<AdminDashboard />} />
            </Routes>
          </div>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
